import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { num, fmtDate } from "@/lib/format";
import { Panel, Pill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SourcePhotoButton } from "@/components/SourcePhotoDialog";

export default function StagingPanel() {
  const [rows, setRows] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState({});
  const load = useCallback(() => {
    api.get("/transactions?status=PENDING&page_size=100").then((r) => setRows(r.data.items));
    api.get("/telegram/inbox").then((r) => setInbox(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirm = async (t) => {
    const body = {};
    if (t.transaction_type === "BON" && t.unit_price == null) {
      if (!price[t.transaction_id]) return toast.error("PRICE PENDING — isi harga bon dulu");
      body.unit_price = parseInt(price[t.transaction_id]);
    }
    try { await api.post(`/transactions/${t.transaction_id}/confirm`, body); toast.success("Dikonfirmasi"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const cancel = async (id) => { try { await api.post(`/transactions/${id}/cancel`, { reason: "Ditolak admin" }); toast.success("Dibatalkan"); load(); } catch (e) { toast.error(apiErr(e)); } };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
    try {
      const { data } = await api.post("/vision/extract", { image_base64: b64, caption: file.name });
      if (data.result === "DUPLICATE") toast.error(`DUPLICATE TRANSACTION DETECTED (${data.duplicate.id})`);
      else {
        const n = data.transactions?.length || 0, r = data.review?.length || 0;
        if (n) toast.success(`AI: ${n} transaksi masuk PENDING`);
        if (r) toast.warning(`${r} baris perlu review admin: ${data.reasons.join("; ")}`);
        if (!n && !r) toast.warning("Tidak ada catatan yang terbaca");
      }
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <Panel title="Uji Foto via Web (pipeline sama dengan Telegram)">
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-slate-300 text-sm cursor-pointer hover:bg-slate-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? "AI membaca foto…" : "Pilih foto catatan"}
          <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} data-testid="vision-upload-input" />
        </label>
        <p className="text-xs text-slate-400 mt-2">Foto di-hash (anti-duplikat), disimpan ke object storage, dibaca AI Vision. Angka tidak jelas → REQUIRES ADMIN REVIEW.</p>
      </Panel>

      <Panel title={`Inbox AI — Perlu Review Admin (${inbox.length})`} testId="inbox-panel">
        {inbox.length === 0 && <div className="text-sm text-slate-400">Tidak ada foto yang menunggu review.</div>}
        <div className="space-y-2">{inbox.map((ib) => <InboxRow key={ib.id} ib={ib} onDone={load} />)}</div>
      </Panel>

      <Panel title={`Staging — Transaksi PENDING (${rows.length})`} testId="staging-panel">
        <p className="text-sm text-slate-500 mb-3">Hanya CONFIRM yang menjadikan transaksi resmi masuk General Ledger.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">{["Tanggal", "TRX", "Worker", "Tipe", "Produk/Barang", "Qty", "Reject", "Sumber", "Aksi"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>{rows.map((t) => (
              <tr key={t.transaction_id} className="border-b border-slate-100" data-testid={`staging-row-${t.transaction_id}`}>
                <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(t.transaction_date)}</td>
                <td className="px-3 py-2.5 mono text-xs">{t.transaction_id}</td>
                <td className="px-3 py-2.5">{t.worker_id} {t.worker_name}</td>
                <td className="px-3 py-2.5"><Pill kind="type" value={t.transaction_type} /></td>
                <td className="px-3 py-2.5">{t.product_name || t.bon_item_name || "-"}</td>
                <td className="px-3 py-2.5 mono">{num(t.quantity)}</td>
                <td className="px-3 py-2.5 mono text-rose-600">{num(t.reject_quantity)}</td>
                <td className="px-3 py-2.5">{t.source_image ? <SourcePhotoButton id={t.transaction_id} /> : <span className="text-xs text-slate-400">Manual</span>}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    {t.transaction_type === "BON" && t.unit_price == null && (
                      <Input type="number" placeholder="Harga/unit" className="h-7 w-28" value={price[t.transaction_id] || ""} onChange={(e) => setPrice({ ...price, [t.transaction_id]: e.target.value })} data-testid={`price-${t.transaction_id}`} />
                    )}
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7" onClick={() => confirm(t)} data-testid={`confirm-${t.transaction_id}`}>Confirm</Button>
                    <Button size="sm" variant="outline" className="h-7" onClick={() => cancel(t.transaction_id)} data-testid={`cancel-${t.transaction_id}`}>Cancel</Button>
                  </div>
                </td>
              </tr>
            ))}{rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Tidak ada transaksi pending.</td></tr>}</tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function InboxRow({ ib, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ex = ib.extraction || {};
  const reprocess = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/telegram/inbox/${ib.id}/reprocess`);
      const n = data.transactions?.length || 0, r = data.review?.length || 0;
      if (n) toast.success(`${n} transaksi masuk PENDING`);
      if (r) toast.warning(`${r} baris masih perlu review`);
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-amber-200 bg-amber-50/50 rounded-md p-3 text-sm" data-testid={`inbox-row-${ib.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-semibold text-slate-800">{ib.source} · {fmtDate(ib.created_at?.slice(0, 10))} · AI: {ex.transaction_type || "?"} {ex.worker_id || ex.worker_name || "?"} {ex.product_code || ""} qty {ex.quantity ?? "?"}</div>
          <div className="text-xs text-rose-700 mt-0.5">{(ib.review_reasons || []).join(" · ")}</div>
        </div>
        <div className="flex gap-2">
          <SourcePhotoButton id={ib.id} />
          <Button size="sm" className="h-7 bg-slate-900 text-white" onClick={() => setOpen(true)} data-testid={`resolve-${ib.id}`}>Lengkapi</Button>
          <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={reprocess} data-testid={`reprocess-${ib.id}`}>{busy ? "Membaca…" : "Proses Ulang"}</Button>
          <Button size="sm" variant="outline" className="h-7 text-rose-600" onClick={async () => { await api.post(`/telegram/inbox/${ib.id}/reject`); toast.success("Ditolak"); onDone(); }} data-testid={`reject-${ib.id}`}>Tolak</Button>
        </div>
      </div>
      <ResolveDialog ib={ib} open={open} onOpenChange={setOpen} onDone={onDone} />
    </div>
  );
}

function ResolveDialog({ ib, open, onOpenChange, onDone }) {
  const ex = ib.extraction || {};
  const [workers, setWorkers] = useState([]);
  const [products, setProducts] = useState([]);
  const [f, setF] = useState({
    worker_id: ex.worker_id || "", transaction_type: ex.transaction_type || "SETOR", product_id: ex.product_code || "",
    quantity: ex.quantity ?? "", reject_quantity: ex.reject_quantity ?? 0, transaction_date: ex.date || "",
    bon_item_name: ex.bon_item_name || "", unit_price: ex.unit_price ?? "", amount: ex.amount ?? "",
  });
  useEffect(() => {
    if (!open) return;
    api.get("/workers").then((r) => setWorkers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, [open]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    try {
      const body = {
        worker_id: f.worker_id, transaction_type: f.transaction_type, transaction_date: f.transaction_date || null,
        product_id: f.product_id || null, quantity: parseInt(f.quantity || 0), reject_quantity: parseInt(f.reject_quantity || 0),
        bon_item_name: f.bon_item_name || null, unit_price: f.unit_price === "" ? null : parseInt(f.unit_price),
        amount: f.amount === "" ? null : parseInt(f.amount),
      };
      const { data } = await api.post(`/telegram/inbox/${ib.id}/resolve`, body);
      toast.success(`${data.transaction_id} masuk PENDING`); onOpenChange(false); onDone();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const isProd = ["AMBIL", "SETOR"].includes(f.transaction_type);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Lengkapi Data dari Foto</DialogTitle></DialogHeader>
        {ex.raw_text && <pre className="text-xs bg-slate-100 rounded p-2 max-h-24 overflow-auto whitespace-pre-wrap">{ex.raw_text}</pre>}
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Worker</Label>
            <Select value={f.worker_id} onValueChange={set("worker_id")}><SelectTrigger data-testid="resolve-worker"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>{workers.map((w) => <SelectItem key={w.worker_id} value={w.worker_id}>{w.worker_id} — {w.worker_name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Tipe</Label>
            <Select value={f.transaction_type} onValueChange={set("transaction_type")}><SelectTrigger data-testid="resolve-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["AMBIL", "SETOR", "BON", "BAYAR"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Tanggal</Label><Input type="date" value={f.transaction_date} onChange={(e) => set("transaction_date")(e.target.value)} data-testid="resolve-date" /></div>
          {isProd && <div><Label>Produk</Label>
            <Select value={f.product_id} onValueChange={set("product_id")}><SelectTrigger data-testid="resolve-product"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent>{products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.product_code} — {p.product_name}</SelectItem>)}</SelectContent></Select></div>}
          {f.transaction_type !== "BAYAR" && <div><Label>Qty</Label><Input type="number" value={f.quantity} onChange={(e) => set("quantity")(e.target.value)} data-testid="resolve-qty" /></div>}
          {f.transaction_type === "SETOR" && <div><Label>Reject</Label><Input type="number" value={f.reject_quantity} onChange={(e) => set("reject_quantity")(e.target.value)} data-testid="resolve-reject" /></div>}
          {f.transaction_type === "BON" && <><div><Label>Nama Barang</Label><Input value={f.bon_item_name} onChange={(e) => set("bon_item_name")(e.target.value)} /></div>
            <div><Label>Harga/Unit (Rp)</Label><Input type="number" value={f.unit_price} onChange={(e) => set("unit_price")(e.target.value)} /></div></>}
          {f.transaction_type === "BAYAR" && <div><Label>Nominal (Rp)</Label><Input type="number" value={f.amount} onChange={(e) => set("amount")(e.target.value)} /></div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button><Button onClick={save} className="bg-slate-900 text-white" data-testid="resolve-submit">Buat PENDING</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
